import Box from '@mui/material/Box';
import { SxProps } from '@mui/material/styles';
import { mdiChevronRight, mdiLightbulbOutline } from '@mdi/js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from 'common/components/Icon';
import {
    ListItem,
    ListItemActionText,
    ListItemMedia,
    ListItemSecondaryAction,
    ListItemText,
} from 'common/components/ListItem';
import { receivedCertificateEndDateNear } from 'common/utils/receivedCertificate';
import { PortfolioItemProgressScoreChip } from 'user/components/PortfolioItemProgressScoreChip';
import { useSkillStatusDescription } from 'skill/hooks/useSkillStatusDescription';
import {
    IBasicFileFragment,
    IPortfolioItemListItem,
    Maybe,
} from 'graphql/types';

const statusIconWrapperStyle: SxProps = {
    position: 'absolute',
    top: 0,
    left: 0,
    pr: '4px',
    pb: '4px',
    backgroundColor: 'common.white',
    borderTopLeftRadius: '4px',
    borderBottomRightRadius: 10,
};

const statusIconInnerStyle: SxProps = {
    display: 'flex',
    borderTopLeftRadius: '4px',
    borderBottomRightRadius: 7,
};

interface ISkill {
    id: string;
    name: string;
    thumbnail?: Maybe<IBasicFileFragment>;
}

interface IProps {
    skill: ISkill;
    portfolioItem?: Maybe<
        Pick<
            IPortfolioItemListItem,
            | 'relatedType'
            | 'score'
            | 'scoreType'
            | 'scoreThreshold'
            | 'scoreMax'
            | 'progress'
            | 'startDate'
            | 'endDate'
            | 'completionDate'
            | 'relatedObjectHasComments'
            | 'relatedObjectHasFiles'
            | 'isCompliant'
        >
    >;
    onClick?(): void;
}

export const SkillPortfolioItemListItem = ({
    skill,
    portfolioItem,
    onClick,
}: IProps) => {
    const [translate] = useTranslation();
    const { name, thumbnail } = skill;

    const image = thumbnail?.url || undefined;

    const statusDescription = useSkillStatusDescription(portfolioItem);

    const wrapperBgColor = useMemo(() => {
        if (!portfolioItem) return undefined;

        const {
            progress,
            score,
            scoreThreshold,
            endDate,
            completionDate,
            isCompliant,
        } = portfolioItem;

        // isCompliant takes precedence over progress/score checks
        if (!!isCompliant) return 'success.main';
        if (isCompliant === false) return 'error.main';

        // In progress (circular progress) — no background
        if (progress !== 1 && !completionDate) return undefined;

        // Nearing expiry
        if (
            endDate &&
            receivedCertificateEndDateNear(endDate) &&
            progress === 1
        )
            return 'warning.main';

        // No score data (grey chip) — no background
        if (score === undefined || score === null) return undefined;

        // Failed (score-based fallback)
        if (scoreThreshold && score < scoreThreshold) return 'error.main';

        // Fulfilled
        return 'success.main';
    }, [portfolioItem]);

    return (
        <ListItem button px={0} py={1.5} onClick={onClick}>
            <Box sx={{ position: 'relative' }}>
                <ListItemMedia color="primary" image={image}>
                    <Icon
                        className="icon"
                        path={mdiLightbulbOutline}
                        size="2.2rem"
                        sizeSm="3rem"
                    />
                </ListItemMedia>

                {!!portfolioItem && (
                    <Box sx={statusIconWrapperStyle}>
                        <Box
                            sx={[
                                statusIconInnerStyle,
                                { backgroundColor: wrapperBgColor },
                            ]}
                        >
                            <PortfolioItemProgressScoreChip
                                withIcon
                                portfolioItem={portfolioItem}
                            />
                        </Box>
                    </Box>
                )}
            </Box>

            <ListItemText primary={name} secondary={statusDescription} />

            <ListItemActionText>{translate('open')}</ListItemActionText>

            <ListItemSecondaryAction hideXs>
                <Icon path={mdiChevronRight} size="4rem" />
            </ListItemSecondaryAction>
        </ListItem>
    );
};
